import React from "react";
import { fromJS, List, Map } from "immutable";
import {
  Alert,
  Box,
  Input,
  Select,
  SelectOption,
  Text,
  Separator,
} from "@showitinc/showit-wc";

import AppConstants from "../../../constants/AppConstants";
import HelpConstants from "../../../constants/HelpConstants";
import LinkProperties from "../../../components/LinkProperties";
import BlockSelectInput from "../../../components/BlockSelectInput";
import SiteBlockSelectInput from "../../../components/SiteBlockSelectInput";
import { INPUT_TYPE_NUMBER } from "../../../constants/ElementConstants";
import { Action, AddAction } from "../../base/Actions";
import { initFormatter } from "../../../utils/PropertyFormatters";
import styles from "./ActionProperties.module.css";
import { parentHasAction } from "@/utils/GroupUtils";
import DataTypes from "@/constants/DataTypes";

const {
  ELEMENT_ACTION_HIDE,
  ELEMENT_ACTION_SHOW,
  ELEMENT_ACTION_SHOW_SITE_BLOCK,
  ELEMENT_ACTION_HIDE_SITE_BLOCK,
  ELEMENT_ACTION_GALLERY,
  GALLERY_ACTION_GOTO,
  GALLERY_ACTION_NEXT,
  GALLERY_ACTION_PREVIOUS,
  GALLERY_ACTION_PLAY,
  GALLERY_ACTION_PAUSE,
  GALLERY_ACTION_TOGGLE,
  SELECT_EMPTY_OPTION_TEXT,
  UPDATE_ACTION_DELETE,
  UPDATE_ACTION_SET,
} = AppConstants;

const ACTION_TYPE = "type";
const ACTION_TARGET = "block";
const ACTIONS = "pubs";
const CLICK_ACTIONS = "click";

const GALLERY_CHANNEL = "channel";
const GALLERY_ACTION = "action";
const GALLERY_IMAGE_SET = "setImg";

const SITE_BLOCK_ACTIONS = [
  <SelectOption
    key={ELEMENT_ACTION_SHOW_SITE_BLOCK}
    value={ELEMENT_ACTION_SHOW_SITE_BLOCK}
  >
    Show Site Canvas
  </SelectOption>,
  <SelectOption
    key={ELEMENT_ACTION_HIDE_SITE_BLOCK}
    value={ELEMENT_ACTION_HIDE_SITE_BLOCK}
  >
    Hide Site Canvas
  </SelectOption>,
  <SelectOption key={ELEMENT_ACTION_GALLERY} value={ELEMENT_ACTION_GALLERY}>
    Gallery
  </SelectOption>,
];

const PAGE_BLOCK_ACTIONS = [
  <SelectOption key={ELEMENT_ACTION_SHOW} value={ELEMENT_ACTION_SHOW}>
    Show Canvas
  </SelectOption>,
  <SelectOption key={ELEMENT_ACTION_HIDE} value={ELEMENT_ACTION_HIDE}>
    Hide Canvas
  </SelectOption>,
  <SelectOption key={ELEMENT_ACTION_GALLERY} value={ELEMENT_ACTION_GALLERY}>
    Gallery
  </SelectOption>,
];

const GALLERY_CHANNELS = Array.from({ length: 9 }).map((_, i) => {
  const num = i + 1;
  return (
    <SelectOption key={num} value={num}>
      {`Gallery ${num}`}
    </SelectOption>
  );
});

const GalleryActionSettings = ({ isLocked, pub, actionId, onChange }) => {
  const channel = pub.get(GALLERY_CHANNEL, "1");
  const targetAction = pub.get(GALLERY_ACTION, "");
  const image = pub.get(GALLERY_IMAGE_SET, "1");

  return (
    <div>
      <Select
        label="Target"
        disabled={isLocked}
        reserveContext
        onSISelect={onChange(`${actionId}.${GALLERY_CHANNEL}`, channel)}
        shift="left"
        value={channel}
      >
        {GALLERY_CHANNELS}
      </Select>
      <Select
        disabled={isLocked}
        label="Action"
        onSISelect={onChange(`${actionId}.${GALLERY_ACTION}`, targetAction)}
        reserveContext
        value={targetAction}
      >
        <SelectOption value="">{SELECT_EMPTY_OPTION_TEXT}</SelectOption>
        <SelectOption value={GALLERY_ACTION_NEXT}>Next</SelectOption>
        <SelectOption value={GALLERY_ACTION_PREVIOUS}>Previous</SelectOption>
        <SelectOption value={GALLERY_ACTION_PLAY}>Play</SelectOption>
        <SelectOption value={GALLERY_ACTION_PAUSE}>Pause</SelectOption>
        <SelectOption value={GALLERY_ACTION_TOGGLE}>Toggle</SelectOption>
        <SelectOption value={GALLERY_ACTION_GOTO}>Set Image</SelectOption>
      </Select>
      {targetAction === GALLERY_ACTION_GOTO && (
        <Input
          disabled={isLocked}
          label="Image"
          min="1"
          onSIBeforeChange={onChange(`${actionId}.${GALLERY_IMAGE_SET}`, image)}
          onSIInput={onChange(`${actionId}.${GALLERY_IMAGE_SET}`, image, false)}
          type={INPUT_TYPE_NUMBER}
          value={image}
        />
      )}
    </div>
  );
};

const DisabledMessage = ({ children }) => (
  <div className={styles.notice}>
    <Text variant="tiny">{children}</Text>
  </div>
);

/**
 * @param {Object} props
 * @param {import("immutable").Map} [props.block]
 * @param {import("immutable").Map} [props.element]
 * @param {boolean} props.isLocked
 * @param {boolean} props.isWP
 * @param {boolean} props.isWPElement
 * @param {boolean} props.inSiteBlock
 * @param {function} props.onPropUpdate
 */
const ElementPropertiesActions = ({
  block = Map(),
  element = Map(),
  isLocked,
  isWP,
  isWPElement,
  inSiteBlock,
  onPropUpdate,
}) => {
  const _addAction = () => {
    const type = inSiteBlock
      ? ELEMENT_ACTION_SHOW_SITE_BLOCK
      : ELEMENT_ACTION_SHOW;
    const pubs = element.get(ACTIONS, Map());
    const click = pubs.get(CLICK_ACTIONS, List()).push(fromJS({ type }));

    onPropUpdate({
      action: UPDATE_ACTION_SET, // reset the link object
      name: ACTIONS,
      origValue: element.get(ACTIONS, null),
      value: pubs.set(CLICK_ACTIONS, click),
    });
  };

  const _removeAction = (propInfo) =>
    onPropUpdate({ ...propInfo, action: UPDATE_ACTION_DELETE });

  const link = element.get("link");
  const clicks = element.getIn([ACTIONS, CLICK_ACTIONS], List());
  const active = (clicks && !clicks.isEmpty()) || link || !isWPElement;
  const disabledByContainer = parentHasAction({ element, block });
  const showGroupChildClickActionsWarning =
    element.get("type") !== DataTypes.GROUP &&
    parentHasAction({ element: element, block });

  const { onChange } = initFormatter(onPropUpdate);

  const GroupChildClickActionsNotice = () => (
    <Box my="2" data-testid="group-child-click-actions-warning">
      <Alert
        variant="info"
        stacked
        weight="bold"
        message="Click Actions are unavailable on grouped elements when the group has a Click Action or Link."
      />
    </Box>
  );

  const WpPlaceholderClickActionsNotice = () => (
    <Box my="2" data-testid="wp-placeholder-click-actions-warning">
      <Alert
        variant="info"
        stacked
        weight="bold"
        message="Click Actions are unavailable when WordPress Placeholder is set in the Text Properties panel above."
      />
    </Box>
  );

  if (!active && isWPElement) {
    return <WpPlaceholderClickActionsNotice />;
  }

  if (showGroupChildClickActionsWarning || disabledByContainer) {
    return <GroupChildClickActionsNotice />;
  }

  const lastIndex = clicks.size - 1;
  const pubPanels = clicks.map((pub, i) => {
    const type = pub.get(ACTION_TYPE);
    const block = pub.get(ACTION_TARGET, "");
    const actionId = `${ACTIONS}.${CLICK_ACTIONS}.${i}`;
    const isLast = i === lastIndex;

    return (
      <Action
        key={actionId}
        index={i}
        onRemoveAction={() => _removeAction({ name: actionId })}
        last={isLast}
        isLocked={isLocked}
      >
        <Select
          disabled={isLocked}
          label="Type"
          onSISelect={onChange(`${actionId}.${ACTION_TYPE}`, type)}
          reserveContext
          shift="left"
          value={type || ""}
        >
          {inSiteBlock ? SITE_BLOCK_ACTIONS : PAGE_BLOCK_ACTIONS}
        </Select>
        {[ELEMENT_ACTION_HIDE, ELEMENT_ACTION_SHOW].includes(type) && (
          <BlockSelectInput
            isLocked={isLocked}
            propName={`${actionId}.${ACTION_TARGET}`}
            value={block}
            label="Canvas"
            onChange={onPropUpdate}
          />
        )}
        {[
          ELEMENT_ACTION_HIDE_SITE_BLOCK,
          ELEMENT_ACTION_SHOW_SITE_BLOCK,
        ].includes(type) && (
          <SiteBlockSelectInput
            disabled={isLocked}
            label="Site Canvas"
            onSISelect={onChange(`${actionId}.${ACTION_TARGET}`, block)}
            reserveContext
            shift="left"
            value={block}
          />
        )}
        {type === ELEMENT_ACTION_GALLERY && (
          <GalleryActionSettings
            isLocked={isLocked}
            pub={pub}
            actionId={actionId}
            onChange={onChange}
          />
        )}
      </Action>
    );
  });

  return (
    <>
      {/*
        TODO: keep this
        <Text className="panel-subheading">
          Link
          <HelpTooltip tooltipKey="click-actions-link" />
        </Text> */}
      <Text className="panel-subheading">Link</Text>
      <LinkProperties
        isLocked={isLocked}
        link={link}
        isWP={isWP}
        onPropUpdate={onPropUpdate}
        isGroup={element.get("type") === DataTypes.GROUP}
      />
      <AddAction onAddAction={_addAction} isLocked={isLocked} />
      {pubPanels.size ? <Separator /> : null}
      {pubPanels}
    </>
  );
};

export default ElementPropertiesActions;
